<template>
    <div class="modal-overlay" v-if="visible" @click.self="closeModal">
        <div class="modal">
            <div class="modal-header">
                <h1 class="modal-title">{{ trailer ? 'Edytuj lawetę' : 'Dodaj lawetę' }}</h1>
                <button class="close-button" @click="closeModal">
                    <font-awesome-icon icon="xmark" />
                </button>
            </div>

            <div class="modal-body">
                <div class="modal-scroll">
                    <form @submit.prevent="handleSubmit">
                        <div class="image-preview-wrapper" v-if="previewUrl || trailer?.image_url">
                            <p>Zdjęcie lawety</p>
                            <img :src="previewUrl || trailer.image_url" alt="Podgląd zdjęcia" class="image-preview" />
                        </div>

                        <div class="image-preview-wrapper" v-if="technicalDrawingUrl || trailer?.technical_drawing_url">
                            <p>Rysunek techniczny</p>
                            <img :src="technicalDrawingUrl || trailer.technical_drawing_url"
                                alt="Podgląd rysunku technicznego" class="image-preview" />
                        </div>

                        <div class="form-grid">
                            <div class="form-group full-width">
                                <label>Zdjęcie</label>
                                <input type="file" accept="image/jpeg,image/png,image/jpg,image/gif"
                                    @change="handleFileChange" />
                            </div>

                            <div class="form-group full-width">
                                <label>Rysunek techniczny</label>
                                <input type="file" accept="image/jpeg,image/png,image/jpg,image/gif"
                                    @change="handleTechnicalDrawingChange" />
                            </div>

                            <div class="form-group">
                                <label>Nazwa *</label>
                                <input v-model="form.name" type="text" required />
                            </div>

                            <div class="form-group">
                                <label>Grupa *</label>
                                <select v-model="form.trailer_group_id" required>
                                    <option disabled value="">Wybierz grupę</option>
                                    <option v-for="group in trailerStore.trailerGroups" :key="group.id"
                                        :value="group.id">
                                        {{ group.name }}
                                    </option>
                                </select>
                            </div>

                            <div class="form-group">
                                <label>Szerokość (cm)</label>
                                <input type="number" v-model="form.width" />
                            </div>

                            <div class="form-group">
                                <label>Wysokość (cm)</label>
                                <input type="number" v-model="form.height" />
                            </div>

                            <div class="form-group">
                                <label>Waga (kg)</label>
                                <input type="number" v-model="form.weight" />
                            </div>

                            <div class="form-group">
                                <label>DMC (kg)</label>
                                <input type="number" v-model="form.dmc" />
                            </div>

                            <div class="form-group">
                                <label>Cena (zł)</label>
                                <input type="number" step="0.01" v-model="form.price" />
                            </div>

                            <div class="form-group full-width">
                                <label>Opis</label>
                                <textarea v-model="form.description" rows="4" />
                            </div>
                        </div>

                        <div class="modal-actions">
                            <button type="submit" class="save-button" :disabled="trailerStore.loading">
                                {{ trailerStore.loading ? 'Zapisywanie...' : trailer ? 'Zapisz zmiany' : 'Dodaj lawetę'
                                }}
                            </button>
                            <button type="button" class="cancel-button" @click="closeModal">Anuluj</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useTrailerStore } from '@/stores/trailerStore'
import { showErrorToast, showWarningToast } from '@/utils/errorUtils'

export default {
    props: {
        visible: Boolean,
        trailer: Object
    },
    data() {
        return {
            trailerStore: useTrailerStore(),
            form: {
                name: '',
                trailer_group_id: '',
                width: '',
                height: '',
                weight: '',
                dmc: '',
                price: '',
                description: '',
                image: null,
                technical_drawing: null

            },
            previewUrl: null,
            technicalDrawingUrl: null
        }
    },
    mounted() {
        if (!this.trailerStore.trailerGroups.length) {
            this.trailerStore.fetchTrailerGroups()
        }
    },
    watch: {
        trailer: {
            immediate: true,
            handler(trailer) {
                if (trailer) {
                    this.form = {
                        name: trailer.name,
                        trailer_group_id: trailer.trailer_group?.id || '',
                        width: trailer.width,
                        height: trailer.height,
                        weight: trailer.weight,
                        dmc: trailer.dmc,
                        price: trailer.price / 100,
                        description: trailer.description || '',
                        image: null,
                        technical_drawing: null
                    }
                    this.previewUrl = trailer.image_url || null
                    this.technicalDrawingUrl = trailer.technical_drawing_url || null
                } else {
                    this.resetForm()
                }
            }
        }
    },
    methods: {
        handleFileChange(event) {
            const file = event.target.files[0]
            if (!file || !file.type.startsWith('image/')) {
                this.form.image = null
                this.previewUrl = null
                showWarningToast('Wybierz poprawny plik graficzny (jpeg, jpg, png, gif).')
                return
            }

            const reader = new FileReader()
            reader.onload = e => {
                const img = new Image()
                img.src = e.target.result
                img.onload = () => {
                    const MAX_WIDTH = 1200
                    const scale = Math.min(1, MAX_WIDTH / img.width)

                    const canvas = document.createElement('canvas')
                    canvas.width = img.width * scale
                    canvas.height = img.height * scale

                    const ctx = canvas.getContext('2d')
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

                    const fileType = file.type === 'image/png' ? 'image/png' : 'image/jpeg'
                    const quality = fileType === 'image/jpeg' ? 0.8 : undefined

                    canvas.toBlob(blob => {
                        if (blob) {
                            const resizedFile = new File([blob], file.name, {
                                type: fileType,
                                lastModified: Date.now(),
                            })

                            this.form.image = resizedFile
                            this.previewUrl = URL.createObjectURL(resizedFile)
                        }
                    }, fileType, quality)
                }
            }
            reader.readAsDataURL(file)
        },

        handleTechnicalDrawingChange(event) {
            const file = event.target.files[0]
            if (!file || !file.type.startsWith('image/')) {
                this.form.technical_drawing = null
                this.technicalDrawingUrl = null
                showWarningToast('Wybierz poprawny plik graficzny (jpeg, jpg, png, gif).')
                return
            }

            const reader = new FileReader()
            reader.onload = e => {
                const img = new Image()
                img.src = e.target.result
                img.onload = () => {
                    const MAX_WIDTH = 1200
                    const scale = Math.min(1, MAX_WIDTH / img.width)

                    const canvas = document.createElement('canvas')
                    canvas.width = img.width * scale
                    canvas.height = img.height * scale

                    const ctx = canvas.getContext('2d')
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

                    const fileType = file.type === 'image/png' ? 'image/png' : 'image/jpeg'
                    const quality = fileType === 'image/jpeg' ? 0.8 : undefined

                    canvas.toBlob(blob => {
                        if (blob) {
                            const resizedFile = new File([blob], file.name, {
                                type: fileType,
                                lastModified: Date.now(),
                            })

                            this.form.technical_drawing = resizedFile
                            this.technicalDrawingUrl = URL.createObjectURL(resizedFile)
                        }
                    }, fileType, quality)
                }
            }
            reader.readAsDataURL(file)
        },

        async handleSubmit() {
            try {
                const formData = new FormData()

                for (const key in this.form) {
                    if (key === 'image') continue

                    const val = this.form[key]
                    if (val !== null && val !== '') {
                        const actualKey = key === 'trailer_group_id' ? 'group' : key
                        const value = key === 'price' ? Math.round(val * 100) : val
                        formData.append(actualKey, value)
                    }
                }

                if (this.form.image instanceof File) {
                    formData.append('image', this.form.image)
                }


                if (this.form.technical_drawing instanceof File) {
                    formData.append('technical_drawing', this.form.technical_drawing)
                }

                if (this.trailer) {
                    await this.trailerStore.updateTrailer(this.trailer.id, formData)
                } else {
                    await this.trailerStore.createTrailer(formData)
                }

                this.$emit('saved')
                this.closeModal()
            } catch (err) {
                console.error('Błąd przy zapisie:', err.response?.data || err)
                showErrorToast(err, 'Wystąpił błąd podczas zapisu lawety.')
            }
        },

        closeModal() {
            this.resetForm()
            this.$emit('close')
        },

        resetForm() {
            this.form = {
                name: '',
                trailer_group_id: '',
                width: '',
                height: '',
                weight: '',
                dmc: '',
                price: '',
                description: '',
                image: null
            }
            this.previewUrl = null
        }
    }
}
</script>

<style scoped lang="scss">
*,
*::before,
*::after {
    box-sizing: border-box;
}

.modal-overlay {
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    padding: 20px;
}

.modal {
    width: 100%;
    max-width: 700px;
    max-height: 90vh;
    background-color: $color-background;
    border-radius: 12px;
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.modal-header {
    padding: 16px 20px;
    background-color: $color-primary;
    color: $color-white;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-title {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0;
}

.close-button {
    background: none;
    border: none;
    color: $color-white;
    font-size: 1.3rem;
    cursor: pointer;
    transition: transform 0.2s ease;

    &:hover {
        transform: scale(1.1);
    }
}

.modal-body {
    flex: 1;
    overflow: hidden;
}

.modal-scroll {
    padding: 20px;
    overflow-y: auto;
    max-height: calc(90vh - 70px);
}

.image-preview-wrapper {
    text-align: center;
    margin-bottom: 1.5rem;

    .image-preview {
        display: block;
        width: 100%;
        height: auto;
        max-width: 100%;
        max-height: 200px;
        border-radius: 8px;
        border: 1px solid $color-grey;
        object-fit: contain;
        background-color: $color-background;
    }
}

.form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;

    >* {
        min-width: 0;
    }

    .form-group.full-width {
        grid-column: span 2;
    }

    @media (max-width: 600px) {
        grid-template-columns: 1fr;

        .form-group.full-width {
            grid-column: span 1;
        }
    }
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
    overflow: hidden;

    label {
        font-weight: 500;
        font-size: 0.95rem;
    }

    input,
    select,
    textarea {
        font-family: 'Inter', sans-serif;
        padding: 10px;
        border: 2px solid $color-grey;
        border-radius: 8px;
        font-size: 1rem;
        transition: border 0.3s ease;
        width: 100%;
        max-width: 100%;

        &:focus {
            border-color: $color-primary;
            outline: none;
        }
    }

    textarea {
        resize: vertical;
    }
}

select {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
}

.modal-actions {
    display: flex;
    gap: 1rem;
    margin-top: 20px;

    button {
        flex: 1;
        padding: 10px 16px;
        border: none;
        border-radius: 8px;
        font-size: 1rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s ease;
    }

    .save-button {
        background-color: $color-primary;
        color: $color-white;

        &:hover {
            background-color: $color-primary-dark;
        }
    }

    .cancel-button {
        background-color: $color-grey;
        color: $color-text;

        &:hover {
            background-color: $color-grey-dark;
        }
    }

    @media (max-width: 600px) {
        flex-direction: column;

        button {
            width: 100%;
        }
    }
}
</style>
